import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  FiMenu,
  FiX,
  FiUser,
  FiLogOut,
  FiSettings,
  FiHome,
  FiCalendar,
  FiFileText,
  FiVideo,
  FiMessageSquare,
  FiTrendingUp,
  FiBriefcase,
  FiTarget,
  FiCreditCard,
  FiUsers,
  FiLayers,
  FiBarChart,
  FiDatabase,
  FiEdit3,
  FiActivity,
  FiDollarSign,
  FiShield,
  FiFolder,
  FiBookOpen,
  FiUpload,
  FiClipboard,
} from 'react-icons/fi';
import {
  getMenuForUserType as getRoleMenu,
  getPathForSlug,
  getUserTypeLabel,
  getHomePathForUser,
  isAdmin,
} from '../utils/userTypeUtils';
import SiteGuide from './SiteGuide';

const LayoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    gap: 0.5rem;
  }
`;

const LogoBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  @media (max-width: 480px) {
    align-items: center;
    text-align: center;
  }
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    gap: 0.25rem;
  }
`;

const LogoIcon = styled.div`
  width: 44px;
  height: 44px;
  background: ${props => props.theme.colors.primary};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.3rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  margin-bottom: 0.1rem;
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 1.1rem;
  }
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 1rem;
  }
`;

const LogoText = styled.span`
  color: ${props => props.theme.colors.primary};
  font-size: 1.7rem;
  font-weight: bold;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const Tagline = styled.div`
  font-size: 0.72rem;
  color: #b0b0b0;
  font-weight: 400;
  margin-top: 0.1rem;
  letter-spacing: 0.03em;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.6rem;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 480px) {
    justify-content: center;
    gap: 0.75rem;
  }
`;

const UserMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.background};
  }
  
  @media (max-width: 768px) {
    gap: 0.375rem;
    padding: 0.375rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.25rem;
    padding: 0.25rem;
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  background: ${props => props.theme.colors.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 0.9rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    width: 24px;
    height: 24px;
    font-size: 0.75rem;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  
  .name {
    font-weight: 600;
    font-size: 0.9rem;
    color: ${props => props.theme.colors.text};
    
    @media (max-width: 768px) {
      font-size: 0.8rem;
    }
    
    @media (max-width: 480px) {
      font-size: 0.75rem;
    }
  }
  
  .role {
    font-size: 0.8rem;
    color: ${props => props.theme.colors.textLight};
    
    @media (max-width: 768px) {
      font-size: 0.7rem;
    }
    
    @media (max-width: 480px) {
      font-size: 0.65rem;
    }
  }
  
  @media (max-width: 480px) {
    .name, .role {
      display: none;
    }
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 1000;
  overflow: hidden;
  
  @media (max-width: 768px) {
    min-width: 180px;
    right: -0.5rem;
  }
  
  @media (max-width: 480px) {
    min-width: 160px;
    right: -1rem;
  }
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: ${props => props.theme.colors.text};
  transition: background 0.3s ease;
  font-size: 0.9rem;
  
  &:hover {
    background: ${props => props.theme.colors.background};
  }
  
  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 0.875rem;
    font-size: 0.85rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  color: ${props => props.theme.colors.error};
  cursor: pointer;
  transition: background 0.3s ease;
  font-size: 0.9rem;
  
  &:hover {
    background: ${props => props.theme.colors.background};
  }
  
  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 0.875rem;
    font-size: 0.85rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.background};
  }
  
  @media (max-width: 768px) {
    display: block;
  }
  
  @media (max-width: 480px) {
    padding: 0.375rem;
  }
`;

const MainContent = styled.main`
  display: flex;
  flex: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.nav`
  width: 250px;
  background: white;
  border-right: 1px solid ${props => props.theme.colors.border};
  padding: 2rem 0;
  transition: transform 0.3s ease;
  overflow-y: auto;
  
  @media (max-width: 1024px) {
    width: 220px;
    padding: 1.5rem 0;
  }
  
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 280px;
    z-index: 1000;
    transform: translateX(${props => props.isOpen ? '0' : '-100%'});
    box-shadow: ${props => props.isOpen ? '2px 0 8px rgba(0, 0, 0, 0.1)' : 'none'};
    padding: 1rem 0;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    padding: 0.75rem 0;
  }
`;

const SidebarOverlay = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
    backdrop-filter: blur(2px);
  }
`;

const CloseButton = styled.button`
  display: none;
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: background 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.background};
  }
  
  @media (max-width: 768px) {
    display: block;
  }
  
  @media (max-width: 480px) {
    top: 0.75rem;
    right: 0.75rem;
  }
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 2rem;
  text-decoration: none;
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.text};
  background: ${props => props.active ? props.theme.colors.background : 'transparent'};
  border-right: 3px solid ${props => props.active ? props.theme.colors.primary : 'transparent'};
  transition: all 0.3s ease;
  font-size: 0.9rem;
  min-width: 0;
  overflow-wrap: break-word;
  
  &:hover {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.primary};
  }
  
  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
  
  @media (max-width: 1024px) {
    padding: 0.625rem 1.5rem;
    font-size: 0.85rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem 1rem;
    font-size: 0.95rem;
  }
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
  padding: 2rem;
  background: ${props => props.theme.colors.background};
  overflow-y: auto;
  overflow-x: hidden;
  min-height: calc(100vh - 80px);
  
  @media (max-width: 1024px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    min-height: calc(100vh - 120px);
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    min-height: calc(100vh - 140px);
  }
`;

const iconMap = {
  // Core icons
  FiHome: <FiHome size={18} />,
  FiUser: <FiUser size={18} />,
  FiCalendar: <FiCalendar size={18} />,
  FiFileText: <FiFileText size={18} />,
  FiBarChart: <FiBarChart size={18} />,
  FiHelpCircle: <FiMessageSquare size={18} />,
  FiMessageSquare: <FiMessageSquare size={18} />,
  FiSettings: <FiSettings size={18} />,
  
  // Candidate-specific icons
  FiVideo: <FiVideo size={18} />,
  FiBookOpen: <FiBookOpen size={18} />,
  FiTrendingUp: <FiTrendingUp size={18} />,
  FiBriefcase: <FiBriefcase size={18} />,
  FiTarget: <FiTarget size={18} />,
  FiCreditCard: <FiCreditCard size={18} />,
  FiFolder: <FiFolder size={18} />,

  // Consultancy-specific icons
  FiUsers: <FiUsers size={18} />,
  FiBuilding: <FiHome size={18} />,
  FiLayers: <FiLayers size={18} />,
  FiBarChart2: <FiBarChart size={18} />,
  FiDatabase: <FiDatabase size={18} />,

  // Admin-specific icons
  FiEdit3: <FiEdit3 size={18} />,
  FiLayers: <FiLayers size={18} />,
  FiActivity: <FiActivity size={18} />,
  FiDollarSign: <FiDollarSign size={18} />,
  FiShield: <FiShield size={18} />,
  FiClipboard: <FiClipboard size={18} />,
  FiUpload: <FiUpload size={18} />,

  // Legacy support
  Dashboard: <FiHome size={18} />,
  Profile: <FiUser size={18} />,
  Interviews: <FiCalendar size={18} />,
  'Job Descriptions': <FiFileText size={18} />,
  Analytics: <FiBarChart size={18} />,
  'Help & Support': <FiMessageSquare size={18} />,
  'Admin Panel': <FiSettings size={18} />,
};

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const currentMenuItems = getRoleMenu(user);

  return (
    <LayoutContainer>
      <Header>
        <LogoBox>
          <Logo to={getHomePathForUser(user)} style={{ textDecoration: 'none', alignItems: 'center', gap: '0.7rem' }}>
            <LogoIcon>EW</LogoIcon>
            <LogoText>EnterWeU</LogoText>
          </Logo>
          <Tagline>{isAdmin(user) ? 'Application control center' : 'Interview prep and talent discovery'}</Tagline>
        </LogoBox>
        
        <HeaderRight>
          <UserMenu onClick={() => setShowUserMenu(!showUserMenu)}>
            <UserAvatar>
              {getInitials(user?.full_name || user?.username || 'U')}
            </UserAvatar>
            <UserInfo>
              <span className="name">{user?.full_name || user?.username}</span>
              <span className="role">{getUserTypeLabel(user)}</span>
            </UserInfo>
            
            {showUserMenu && (
              <DropdownMenu>
                <DropdownItem to="/profile">
                  <FiUser size={16} />
                  Profile
                </DropdownItem>
                <DropdownItem to="/settings">
                  <FiSettings size={16} />
                  Settings
                </DropdownItem>
                <LogoutButton onClick={handleLogout}>
                  <FiLogOut size={16} />
                  Logout
                </LogoutButton>
              </DropdownMenu>
            )}
          </UserMenu>
          
          <MobileMenuButton onClick={() => setShowMobileMenu(true)}>
            <FiMenu size={24} />
          </MobileMenuButton>
        </HeaderRight>
      </Header>

      <MainContent>
        <SidebarOverlay 
          isOpen={showMobileMenu} 
          onClick={() => setShowMobileMenu(false)} 
        />
        
        <Sidebar isOpen={showMobileMenu}>
          <CloseButton onClick={() => setShowMobileMenu(false)}>
            <FiX size={24} />
          </CloseButton>
          
          {currentMenuItems.map((item) => (
            <NavItem
              key={item.slug}
              to={item.path || getPathForSlug(item.slug, user)}
              active={location.pathname === (item.path || getPathForSlug(item.slug, user))}
              onClick={() => setShowMobileMenu(false)}
            >
              {iconMap[item.icon] || iconMap[item.title] || <FiHome size={18} />}
              {item.title}
            </NavItem>
          ))}
        </Sidebar>
        
        <Content>
          {children}
        </Content>
      </MainContent>

      <SiteGuide hidden={isAdmin(user)} user={user} />
    </LayoutContainer>
  );
};

export default Layout;